/**
 * Writing places: the edits that put items and frames where a gesture left them, moving what
 * was dropped into the frame it landed in, and relaying the layout frames they end up in.
 */
import { unwrap } from "solid-js/store";
import type { BoardPlace } from "@protocol/BoardPlace";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { BoardCtx } from "./context";
import { asPatch, carried, frameAround } from "./geometry";

/** The edits writing `places` (and, for what was `dropped`, the frame it landed in). */
export function placeEdits(ctx: BoardCtx, places: Map<string, BoardPlace>, dropped: string[] = []): GraphEdit[] {
  const g = unwrap(ctx.deps.graph.state.graph);
  const moving = new Set(places.keys());
  const landed = { ...ctx.content(), places: new Map([...ctx.content().places, ...places]) };
  landed.frames = landed.frames.map((f) => ({ ...f, place: landed.places.get(f.id)! }));
  const edits: GraphEdit[] = [];
  /** The patches of the nodes written, by id (a layout frame's may add to them). */
  const patches = new Map<string, Record<string, unknown>>();
  /** Where the nodes written end up: their new frame, or the one they were in. */
  const homes = new Map<string, string | null>();
  for (const [id, place] of places) {
    const node = g.nodes.find((n) => n.id === id);
    const patch: Record<string, unknown> = { board: asPatch(place) };
    if (node && dropped.includes(id) && (!node.group || ctx.isFrame(node.group))) {
      const into = frameAround(landed, place, moving);
      if ((into ?? null) !== (node.group ?? null)) patch.group = into ?? "";
    }
    if (node) {
      homes.set(id, patch.group === undefined ? (node.group ?? null) : (patch.group as string) || null);
      patches.set(id, patch);
      edits.push({ edit: "update_node", id, patch });
    } else {
      const group = g.groups.find((x) => x.id === id);
      if (!group) continue;
      if (dropped.includes(id)) {
        const into = frameAround(landed, place, new Set([id, ...carried(g, [id])]));
        if ((into ?? null) !== (group.parent ?? null) && (!group.parent || ctx.isFrame(group.parent))) patch.parent = into ?? "";
      }
      edits.push({ edit: "update_group", id, patch });
    }
  }
  for (const frame of new Set([...homes.values()].filter((f): f is string => !!f && ctx.laysOut(f)))) edits.push(...ctx.relayEdits(frame, places, homes, patches));
  return edits;
}
